import { Box, Flex, Text, Tooltip } from '@modusign/design-system';
import { ReactNode } from 'react';

import CircleMarker from '@app/components/CircleMarker';
import FieldLocatorBadge from '@app/components/FieldLocatorBadge';

import './SetupButton.scss';

interface SetupButtonProps {
  children: ReactNode;
  count: number;
  leftIcon: ReactNode;
  readOnly: boolean;
  testId: string;
  text: string;
  onClick: () => void;
}

const defaultProps = {
  children: null,
  count: 0,
  leftIcon: <CircleMarker />,
  readOnly: false,
  testId: 'setup-button',
  text: '텍스트',
  onClick() {},
};

function SetupButton({
  children,
  count,
  leftIcon,
  readOnly,
  testId,
  text,
  onClick,
}: SetupButtonProps) {
  const handleClick = () => {
    if (!readOnly) {
      onClick();
    }
  };

  return (
    <Tooltip content={<Text>{children}</Text>} disabled={readOnly || !children} placement="right">
      <Box
        className="FieldLocator"
        display={readOnly ? 'block' : 'inline-block'}
        position="relative"
        width="100%"
      >
        <Flex
          alignItems="center"
          as="button"
          data-testid={testId}
          justifyContent="space-between"
          sx={
            readOnly
              ? {
                  bg: 'white',
                  height: 36,
                  marginBottom: 2,
                  outline: 'none',
                  paddingBottom: '5px',
                  textAlign: 'left',
                  width: '100%',
                  '&:hover': {
                    bg: 'lightGray50',
                  },
                }
              : {
                  bg: 'white',
                  border: '1px solid',
                  borderColor: 'buttonBorderNeutralBase',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  height: '36px',
                  marginBottom: 2,
                  outline: 'none',
                  padding: '8px 8px 8px 12px',
                  textAlign: 'left',
                  width: '100%',
                  '&:hover': {
                    bg: 'lightGray50',
                  },
                }
          }
          tabIndex={0}
          type="button"
          onClick={handleClick}
        >
          <Box height="100%" width="100%">
            {!readOnly && (
              <Box mr={3} sx={{ float: 'left' }} textAlign="center" width="20px">
                {leftIcon}
              </Box>
            )}
            <Text
              as="span"
              color="textNeutralLight"
              fontSize="md"
              fontWeight="medium"
              lineHeight="18px"
              sx={{ marginRight: 3 }}
            >
              {text}
            </Text>
          </Box>
          {count > 0 && <FieldLocatorBadge>{count}</FieldLocatorBadge>}
        </Flex>
      </Box>
    </Tooltip>
  );
}

SetupButton.defaultProps = defaultProps;

export default SetupButton;
